import { supabase } from '../lib/supabase';

const BUCKET = 'assets';
const PREFIX = 'budget-items';

function sanitizeFileName(name: string): string {
  const ext = name.match(/\.[^.]+$/)?.[0]?.toLowerCase() || '';
  const base = name
    .replace(/\.[^.]+$/, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'image';
  return `${base}${ext}`;
}

export async function uploadBudgetItemImage(
  budgetId: string,
  file: File,
  itemIndex?: number
): Promise<string> {
  const safe = sanitizeFileName(file.name);
  const suffix = itemIndex !== undefined ? `item-${itemIndex}` : 'item';
  const path = `${PREFIX}/${budgetId}/${suffix}-${Date.now()}-${Math.random().toString(36).slice(2)}-${safe}`;

  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;

  const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return publicUrl;
}

export async function removeBudgetItemImageFromStorage(imageUrl?: string | null): Promise<void> {
  if (!imageUrl) return;
  try {
    const marker = `/storage/v1/object/public/${BUCKET}/`;
    const idx = imageUrl.indexOf(marker);
    if (idx === -1) return;
    const path = decodeURIComponent(imageUrl.slice(idx + marker.length).split('?')[0]);
    const { error } = await supabase.storage.from(BUCKET).remove([path]);
    if (error) console.error('Error removing budget item image:', error);
  } catch (e) {
    console.error('Error parsing budget item image path:', e);
  }
}

export interface PdfImageData {
  data: string;
  format: 'JPEG' | 'PNG' | 'WEBP';
  width: number;
  height: number;
}

export async function loadImageForPdf(url: string): Promise<PdfImageData> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`No se pudo cargar la imagen (${response.status})`);

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('No se pudo procesar la imagen'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        const mime = blob.type || 'image/jpeg';
        const dataUrl = canvas.toDataURL(mime, 0.92);
        const format: PdfImageData['format'] =
          mime.includes('png') ? 'PNG' : mime.includes('webp') ? 'WEBP' : 'JPEG';
        resolve({
          data: dataUrl,
          format,
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.onerror = () => reject(new Error('Error al decodificar la imagen'));
      img.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
